/** Listening-practice audio transport for Hören items.
 *
 * The audio is EITHER a real recording (`src`, a generated MP3) or, until
 * one exists, the browser's German TTS reading the transcript (`text`).
 *
 * There is deliberately NO "show transcript" control here. Reading the
 * script during a listening exercise defeats it — the transcript is
 * answer-adjacent and is shown only AFTER answering, in the result view.
 * When a recording exists the server does not even send the transcript to
 * the page; `text` is present only in the TTS-fallback case, and even then
 * it is used to make a sound, never displayed.
 */
import { useEffect, useRef, useState } from "react";
import { useT } from "../../i18n";
import { cancelSpeech, pauseSpeech, resumeSpeech, speak, ttsAvailable } from "../../utils/tts";
import * as media from "../../api/media";

type Status = "idle" | "playing" | "paused";

//: Slower, never faster. A Hoeren recording is read at exam pace, and a
//: learner who cannot follow it needs it stretched, not a stopwatch - the
//: complaint that produced this list was a child saying "it is too fast".
//: Half speed is the floor: below that German stops sounding like speech
//: and the practice value goes with it.
const RATES = [0.5, 0.75, 0.9, 1];

export function AudioPlayer({ text, src }: { text?: string; src?: string }) {
  const t = useT();
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [audioFailed, setAudioFailed] = useState(false);
  const available = ttsAvailable();
  const [status, setStatus] = useState<Status>("idle");
  const [progress, setProgress] = useState(0);
  const [rate, setRate] = useState(1);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  /** Apply the chosen speed to a real recording.
   *
   *  `preservesPitch` is the whole point: without it a slowed voice drops
   *  in pitch and stops sounding like German, which is worse than useless
   *  for listening practice. Safari spells it with a prefix and older
   *  builds have neither, so all three spellings are set and none is
   *  required to exist. */
  const applyRate = (el: HTMLAudioElement | null, r: number) => {
    if (!el) return;
    const any = el as HTMLAudioElement & {
      preservesPitch?: boolean;
      mozPreservesPitch?: boolean;
      webkitPreservesPitch?: boolean;
    };
    any.preservesPitch = true;
    any.mozPreservesPitch = true;
    any.webkitPreservesPitch = true;
    el.playbackRate = r;
  };

  // A new blob resets the element, so the chosen speed has to be re-applied
  // rather than assumed to have survived.
  useEffect(() => {
    applyRate(audioRef.current, rate);
  }, [rate, blobUrl, src]);

  // Stop any speech when the learner leaves the item.
  useEffect(() => () => cancelSpeech(), []);

  // A recording served by OUR api needs the session token, which a plain
  // `<audio src>` cannot send. Fetch it and play it from a blob instead.
  // Anything on the static mount is used directly.
  const authed = Boolean(src && media.needsAuth(src));
  useEffect(() => {
    if (!src || !authed) return;
    let cancelled = false;
    let url: string | null = null;
    setAudioFailed(false);
    media
      .fetchMedia(src)
      .then((blob) => {
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setBlobUrl(url);
      })
      // No recording available: fall through to the browser's own voice
      // reading the transcript, which is what happened before any of this.
      .catch(() => {
        if (!cancelled) setAudioFailed(true);
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);  // an object URL leaks until revoked
      setBlobUrl(null);
    };
  }, [src, authed]);

  const start = (r: number) => {
    if (!text) return;
    setProgress(0);
    speak(text, {
      rate: r,
      onEnd: () => {
        setStatus("idle");
        setProgress(1);
      },
      onBoundary: setProgress,
    });
    setStatus("playing");
  };

  const onToggle = () => {
    if (status === "playing") {
      pauseSpeech();
      setStatus("paused");
    } else if (status === "paused") {
      resumeSpeech();
      setStatus("playing");
    } else {
      start(rate);
    }
  };

  const onRestart = () => {
    cancelSpeech();
    start(rate);
  };

  const onRate = (r: number) => {
    setRate(r);
    if (status !== "idle") {
      cancelSpeech();
      start(r); // speechSynthesis can't change rate mid-utterance — restart
    }
  };

  return (
    <div className="rounded-control border border-line bg-canvas p-3">
      {blobUrl || (src && !authed) ? (
        <div>
          <audio
            ref={audioRef}
            data-testid="native-audio"
            controls
            preload="none"
            src={blobUrl ?? src}
            className="w-full"
            onLoadedMetadata={(e) => applyRate(e.currentTarget, rate)}
          />
          {/* The browser's own controls have no speed on a phone, which is
              where most of this is listened to. */}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted">
              {t("player.speed")}
            </span>
            {RATES.map((r) => (
              <button
                key={r}
                onClick={() => setRate(r)}
                aria-pressed={rate === r}
                className={`rounded-control px-2 py-1 text-xs font-semibold ${
                  rate === r ? "bg-brand-soft text-brand-dark" : "text-muted hover:text-brand"
                }`}
              >
                {r === 1 ? t("player.normal") : `${Math.round(r * 100)}%`}
              </button>
            ))}
          </div>
        </div>
      ) : authed && !audioFailed ? (
        <p className="text-sm text-muted">{t("player.loading")}</p>
      ) : available && text ? (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={onToggle}
              aria-label={status === "playing" ? t("player.pause") : t("player.listen")}
              className="rounded-control bg-brand px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-dark"
            >
              <span aria-hidden>{status === "playing" ? "⏸" : "▶"}</span>{" "}
              {status === "playing" ? t("player.pause") : t("player.play")}
            </button>
            <button
              onClick={onRestart}
              aria-label={t("player.restart")}
              className="text-sm font-semibold text-muted hover:text-brand"
            >
              <span aria-hidden>↺</span> {t("player.restart")}
            </button>
            <div className="ml-auto flex flex-wrap items-center gap-1">
              {RATES.map((r) => (
                <button
                  key={r}
                  onClick={() => onRate(r)}
                  aria-pressed={rate === r}
                  className={`rounded-control px-2 py-1 text-xs font-semibold ${
                    rate === r ? "bg-brand-soft text-brand-dark" : "text-muted hover:text-brand"
                  }`}
                >
                  {r === 1 ? t("player.normal") : `${Math.round(r * 100)}%`}
                </button>
              ))}
            </div>
          </div>
          <div
            className="mt-3 h-1.5 overflow-hidden rounded-full bg-line"
            role="progressbar"
            aria-label={t("player.listen")}
            aria-valuenow={Math.round(progress * 100)}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div
              className="h-full rounded-full bg-brand transition-[width] duration-200"
              style={{ width: `${Math.round(progress * 100)}%` }}
            />
          </div>
        </>
      ) : (
        <p className="text-sm text-muted">{t("player.audioUnavailable")}</p>
      )}
    </div>
  );
}
